import React, {useEffect, useRef, useState} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import {createFragmentContainer, graphql} from 'react-relay';
import {LockIcon} from '@/web-components';
import {BUTTON_COLOR, BUTTON_STYLE} from '../../../constants';
import {CSS_CONSTANTS} from '../../../css_variables';
import DirectApi from '../../../directApi';
import Dropdown from '../../../forecast-app/shared/components/dropdowns/dropdown';
import {MODAL_TYPE, showModal} from '../../../forecast-app/shared/components/modals/generic_modal_conductor';
import Switch from '../../../forecast-app/shared/components/switch/switch';
import {createToast} from '../../../forecast-app/shared/components/toasts/another-toast/toaster';
import {getLocalStorageSilent, setLocalStorageSilent} from '../../../forecast-app/shared/util/LocalStorageUtil';
import {hasPermission} from '../../../forecast-app/shared/util/PermissionsUtil';
import Util from '../../../forecast-app/shared/util/util';
import CreateXeroInvoiceMutation from '../../../mutations/CreateXeroInvoiceMutation';
import {PERMISSION_TYPE} from '../../../Permissions';
import {
	MessageContainer,
	PadLockContainer,
	UneditableMessageBox,
} from '../../../styles/sections/insights/invoicing/invoicing_page_styled';
import {
	ContentWrapper,
	InvoiceDataLine,
	InvoiceDataLineTwo,
	InvoiceDropdownContainer,
	SwitchTitle,
	SwitchWrapper,
} from '../../../styles/v2/invoice_modals_v2_styled';
import {hideLoader, showLoader} from '../../global_loader';
import {FlexColumn, FlexRow, SubHeading, Text} from '@forecast-it/design-system';
import InvoiceEntriesList from '../create_invoice_modal_v2/steps/invoice_entries_list';
import XeroExportLinesTable from '../export_invoice_modal/XeroExportLinesTable';
import {
	applyContactTaxRate,
	applyLegacyLineRows,
	hasUnresolvableAccount,
	isConfigStaleForClient,
	isXeroExportBlocked,
	EMPTY_LINE_CONFIG,
	fetchXeroLineConfig,
	nextLinesOnTenantLoad,
	nextTenantId,
	selectedTenantFor,
	shouldUseXeroLinesTable,
	toLegacyLineRows,
	toPreFlagAccountMap,
	toXeroAccountMap,
} from '../export_invoice_modal/ExportInvoiceModal.xeroLines';
import GenericModal from '../generic_modal';

/**
 * Subtotal / Discount / Tax / Total for the flag-on path.
 *
 * `InvoiceEntriesList` renders these itself, from `Util.getInvoiceTotals`, and this modal has no
 * totals block of its own - so swapping that component out for the new line table would leave the
 * user exporting an invoice with no total anywhere on screen. Same source of truth, same four
 * labels; built from the design system so it matches the table above it rather than the legacy
 * styling around it, which is what ExportInvoiceModal already does.
 */
const XeroExportTotals = ({lines, currency, intl}) => {
	const totals = Util.getInvoiceTotals(lines);
	const symbol = Util.GetCurrencySymbol(currency);
	const amount = value => Util.getFormattedNumberWithCurrency(symbol, value, intl);

	return (
		<FlexRow alignItems={'end'} alignSelf={'end'}>
			<FlexColumn gap={'s'}>
				<FlexRow gap={'xxl'} justifyContent={'space-between'}>
					<FormattedMessage id={'common.subtotal'} />:<Text>{amount(totals.subtotal)}</Text>
				</FlexRow>
				<FlexRow gap={'xxl'} justifyContent={'space-between'}>
					<FormattedMessage id={'project_section.xero_modal_table_discount'} />:<Text>{amount(totals.discount)}</Text>
				</FlexRow>
				<FlexRow gap={'xxl'} justifyContent={'space-between'}>
					<FormattedMessage id={'common.tax'} />:<Text>{amount(totals.tax)}</Text>
				</FlexRow>
				<FlexRow gap={'xxl'} justifyContent={'space-between'}>
					<SubHeading>
						<FormattedMessage id={'common.total_amount'} />
					</SubHeading>
					<SubHeading>{amount(totals.total)}</SubHeading>
				</FlexRow>
			</FlexColumn>
		</FlexRow>
	);
};

const xeroExportModal = ({closeModal, viewer, xeroClients}) => {
	const intl = useIntl();
	const {formatMessage} = intl;
	const {invoice, projects} = viewer;
	const mappedProjects = projects.edges.map(p => ({...p.node}));
	const clientProjects = mappedProjects.filter(project =>
		project.client && invoice.client ? project.client.id === invoice.client.id : false
	);

	const getInitialInvoiceLines = () => {
		const edges = invoice?.entries?.edges ?? [];
		return edges.map(e => ({
			...e.node,
			projectId: e.node.project ? e.node.project.id : null,
			quantity: e.node.quantityText,
		}));
	};

	/*
	 * Resolved once per render. The ticket asks for the check "at the point where the invoice line
	 * decides which path to call"; scattering the call made it five separate decisions that could
	 * in principle disagree.
	 */
	const useLinesTable = shouldUseXeroLinesTable(true);

	const [invoiceLines, setInvoiceLines] = useState(getInitialInvoiceLines);
	const [xeroAccounts, setXeroAccounts] = useState([]);
	const [xeroLineConfig, setXeroLineConfig] = useState(EMPTY_LINE_CONFIG);
	// The organisation the on-screen configuration belongs to. Three requests go out per load and
	// the client can be switched while they are in flight, so without this an earlier tenant's
	// accounts could land last and be shown while the export posts to a different organisation.
	// The last organisation we actually knew about; an unknown one must not erase it.
	const lastTenant = useRef(null);
	// The organisation of the request now in flight, for discarding a stale response.
	const requestedTenant = useRef(null);

	const [xeroContacts, setXeroContacts] = useState([]);
	const [selectedClient, setSelectedClient] = useState(
		xeroClients.find(client => invoice.client && client.value === invoice.client.id)
	);
	const [selectedClientTaxRate, setSelectedClientTaxRate] = useState(null);
	// The organisation the CURRENTLY selected client resolves to, which is not necessarily the one
	// the loaded lists came from - see isConfigStaleForClient. Read on every render, so it has to
	// survive xeroContacts holding a failed read's error body rather than an array.
	const selectedXeroTenantId = selectedTenantFor(xeroContacts, selectedClient);
	/*
	 * True from the start when there is a client to load for. loadXeroLineConfig only runs after
	 * xero/contacts resolves - a live Xero call - so starting at false meant every line read
	 * "No accounts imported" for the whole of that window.
	 */
	const [xeroLineConfigLoading, setXeroLineConfigLoading] = useState(() => !!selectedClient && useLinesTable);

	/*
	 * `contactTaxRate` is a parameter rather than a read of `selectedClientTaxRate`, because both
	 * call sites run in the same tick as the setSelectedClientTaxRate that gives it its new value.
	 * Reading the state here would clear the lines onto the PREVIOUS contact's rate.
	 */
	const loadXeroLineConfig = (tenantId, contactTaxRate) => {
		if (!useLinesTable) return;
		/*
		 * A different client can belong to a different Xero organisation, and the ids already on
		 * the lines belong to the previous one. Reloading the dropdowns alone would leave those
		 * ids in place, visibly unresolved but still exported.
		 *
		 * Two refs, because they mean different things. `lastTenant` is the last organisation we
		 * actually knew, which an unknown one must not erase or A -> unknown -> B stops clearing.
		 * `requestedTenant` is the organisation of the request now in flight, which is the raw
		 * argument - conflating them left the response guard comparing A against undefined, so
		 * the loading state never cleared.
		 *
		 * The decision is taken here rather than inside the updater: writing a ref from a state
		 * updater is impure, and under StrictMode's double invoke the second call would see the
		 * ref already advanced and return lines that were never cleared.
		 */
		const previousTenant = lastTenant.current;
		lastTenant.current = nextTenantId(previousTenant, tenantId);
		requestedTenant.current = tenantId;
		/*
		 * The contact's rate goes in so a cleared line lands on it rather than on 0. The lines are
		 * still going to be taxed when they reach Xero; an invoice total that reads as tax-free
		 * because the user switched organisation is a number someone could send to a client.
		 */
		setInvoiceLines(lines => nextLinesOnTenantLoad(previousTenant, tenantId, lines, contactTaxRate).lines);
		// Cleared first, so a slow load shows nothing rather than the previous organisation's.
		setXeroLineConfig(EMPTY_LINE_CONFIG);
		setXeroLineConfigLoading(true);
		fetchXeroLineConfig(tenantId).then(config => {
			if (requestedTenant.current === tenantId) {
				setXeroLineConfig(config);
				setXeroLineConfigLoading(false);
			}
		});
	};

	const [selectedXeroAccount, setSelectedXeroAccount] = useState('-1');
	const [exporting, setExporting] = useState(false);
	const selectXeroAccount = account => {
		setSelectedXeroAccount(account.value);
		invoiceLines.forEach(line => {
			line.xeroAccount = account.value;
			line.tax = selectedClientTaxRate != null ? selectedClientTaxRate : account.taxRate;
			line.accountCode = account.code;
		});
	};

	let xeroProjectInDescription = getLocalStorageSilent('forecast_xeroProjectInDescription') === 'true';

	const [projectInDescription, setProjectInDescription] = useState(xeroProjectInDescription);

	const toggleProjectInDescription = () => {
		const val = !projectInDescription;
		setLocalStorageSilent('forecast_xeroProjectInDescription', val);
		setProjectInDescription(val);
	};

	const getXeroAccounts = async tenantId => {
		setXeroAccounts([{value: '-1', label: intl.formatMessage({id: 'common.loading'})}]);
		setSelectedXeroAccount('-1');

		try {
			return await DirectApi.Fetch_Raw(`xero/accounts/${tenantId}`).then(result => {
				if (result.status === 403) {
					return false;
				}

				result.json().then(json =>
					setXeroAccounts(
						json.map(account => ({
							value: account.AccountID,
							label: account.Name,
							taxRate: account.DefaultTaxRate,
							type: account.Type,
							code: account.Code,
						}))
					)
				);

				return true;
			});
		} catch (error) {
			//TODO: Show error?
		}
	};

	const getXeroContacts = async () => {
		try {
			return await DirectApi.Fetch_Raw(`xero/contacts`).then(result => {
				if (result.status === 403) {
					return false;
				}

				return result.json().then(json => {
					setXeroContacts(json);
					if (selectedClient) {
						const xeroContact = json?.find(xeroContact => xeroContact.ContactID === selectedClient.xeroContactId);
						setSelectedClientTaxRate(xeroContact?.DefaultTaxRate);
						invoiceLines.forEach(line => {
							if (useLinesTable) {
								applyContactTaxRate(line, xeroContact?.DefaultTaxRate);
							} else {
								line.tax = xeroContact.DefaultTaxRate;
							}
						});
					}
					return json;
				});
			});
		} catch (error) {
			//TODO: Show error?
		}
	};

	const selectClient = client => {
		setSelectedClient(client);
		const xeroContact = xeroContacts?.find(xeroContact => xeroContact.ContactID === client.xeroContactId);

		setSelectedClientTaxRate(xeroContact?.DefaultTaxRate);
		if (xeroContact?.DefaultTaxRate != null) {
			invoiceLines.forEach(line => {
				// Only under the flag: the precedence rule is the flag-on one, and off it this
				// path must run exactly the code it ran before.
				if (useLinesTable) {
					applyContactTaxRate(line, xeroContact.DefaultTaxRate);
				} else {
					line.tax = xeroContact.DefaultTaxRate;
				}
			});
		}

		if (xeroContact) {
			getXeroAccounts(xeroContact.TenantId);
			loadXeroLineConfig(xeroContact.TenantId, xeroContact?.DefaultTaxRate);
		} else if (useLinesTable) {
			/*
			 * No contact resolves no organisation, so there is nothing to offer. Leaving the
			 * previous organisation's lists on screen would let them be selected for this client.
			 *
			 * The in-flight request is cancelled too: without this its response still matches
			 * `requestedTenant` and puts the previous organisation's lists straight back.
			 */
			requestedTenant.current = null;
			setXeroLineConfig(EMPTY_LINE_CONFIG);
			setXeroLineConfigLoading(false);
		}
	};

	const getXeroTaxRate = xeroAccountId => {
		if (selectedClientTaxRate != null) {
			return selectedClientTaxRate;
		}

		const xeroAccount = xeroAccounts.find(acc => acc.value === xeroAccountId);
		return xeroAccount?.taxRate || 0;
	};

	let xeroClientsSpliced = xeroClients;

	if (xeroClients && xeroClients.length !== 0 && xeroContacts && Array.isArray(xeroContacts) && xeroContacts.length !== 0) {
		xeroClientsSpliced = xeroClients.map(client => {
			// find contact
			const contact = xeroContacts.find(c => c.ContactID === client.xeroContactId);

			return {...client, label: `${client.label} (${contact?.Organization})`};
		});
	}

	const content = (
		<ContentWrapper minHeight="600px">
			<UneditableMessageBox style={{marginBottom: '30px'}}>
				<PadLockContainer>
					<LockIcon size={LockIcon.SIZE.LARGE} color={CSS_CONSTANTS.v2_text_gray} />
				</PadLockContainer>
				<MessageContainer>{formatMessage({id: 'invoicing.cannot_edit_warning_exporting'})}</MessageContainer>
			</UneditableMessageBox>
			<InvoiceDataLineTwo>
				<InvoiceDropdownContainer>
					<Dropdown
						onChange={option => selectClient(option)}
						options={xeroClientsSpliced}
						value={selectedClient ? selectedClient.value : null}
						label={formatMessage({id: 'common.customer'})}
						placeholder={formatMessage({id: 'common.customer'})}
					/>
				</InvoiceDropdownContainer>
				{/*
				 * Hidden under the flag, for the same reason as in ExportInvoiceModal: it is fed by
				 * the pre-flag route, which returns every account class unfiltered, and the line's
				 * own Account column replaces it.
				 */}
				{!useLinesTable && (
					<InvoiceDropdownContainer>
						<Dropdown
							onChange={option => selectXeroAccount(option)}
							options={xeroAccounts}
							value={selectedXeroAccount}
							label={formatMessage({id: 'integrations.xero.xero_account'})}
							placeholder={formatMessage({id: 'integrations.xero.xero_account'})}
							disabled={xeroAccounts.length === 0}
						/>
					</InvoiceDropdownContainer>
				)}
			</InvoiceDataLineTwo>
			<InvoiceDataLine>
				<SwitchWrapper>
					<SwitchTitle>
						<FormattedMessage id="invoicing.xero.project_in_description" />
					</SwitchTitle>
					<Switch
						onChange={() => toggleProjectInDescription()}
						checked={projectInDescription}
						sliderWidth={30}
						sliderHeight={18}
					/>
				</SwitchWrapper>
			</InvoiceDataLine>
			{useLinesTable ? (
				<>
					<XeroExportLinesTable
						rows={toLegacyLineRows(invoiceLines, projectInDescription)}
						currency={invoice.currency}
						accounts={xeroLineConfig.accounts}
						items={xeroLineConfig.items}
						trackingCategories={xeroLineConfig.trackingCategories}
						onRowsChange={rows => setInvoiceLines(lines => applyLegacyLineRows(lines, rows, selectedClientTaxRate))}
						disabled={exporting}
						loading={xeroLineConfigLoading}
						failed={xeroLineConfig.failed}
					/>
					<XeroExportTotals lines={invoiceLines} currency={invoice.currency} intl={intl} />
				</>
			) : (
				<InvoiceEntriesList
					invoiceExporting={true}
					lines={invoiceLines.map(line => {
						const convertedLine = {...line};
						convertedLine.tax = getXeroTaxRate(convertedLine.xeroAccount);
						if (projectInDescription && convertedLine.project)
							convertedLine.description = `(P${convertedLine.project.companyProjectId}-${convertedLine.project.name}) ${convertedLine.description}`;
						return convertedLine;
					})}
					projects={mappedProjects}
					clientProjects={clientProjects}
					currency={invoice.currency}
					setInvoiceLines={setInvoiceLines}
					invoice={invoice}
					integrationData={{
						xero: {
							enabled: true,
							accounts: xeroAccounts,
						},
					}}
				/>
			)}
		</ContentWrapper>
	);

	const onInvoiceExport = () => {
		// Get tenantId
		const xeroContact = xeroContacts?.find(xeroContact => xeroContact.ContactID === selectedClient.xeroContactId);

		setExporting(true);
		showLoader();
		// Export invoice to Xero
		Util.CommitMutation(
			CreateXeroInvoiceMutation,
			{
				invoiceId: invoice.id,
				forecastClientId: selectedClient.value,
				// Under the flag the line also carries an item, a tax type and tracking; off it,
				// this is byte-for-byte the map built before.
				accountMap: useLinesTable ? toXeroAccountMap(invoiceLines) : toPreFlagAccountMap(invoiceLines),
				tenantId: xeroContact.TenantId,
				projectInDescription: projectInDescription,
			}, // Toast
			result => {
				setExporting(false);
				closeModal();
				hideLoader();
				if (result.createXeroInvoice.success) {
					createToast({duration: 5000, message: intl.formatMessage({id: 'integrations.xero.invoice_created'})});
				} else {
					showModal({
						type: MODAL_TYPE.WARNING,
						warningMessageId: 'invoicing.not_exported',
						warningInformation: [result.createXeroInvoice.errorMessage || 'The invoice could not be exported.'],
						buttons: [
							{
								text: formatMessage({id: 'common.ok'}),
								style: BUTTON_STYLE.FILLED,
								color: BUTTON_COLOR.WHITE,
							},
						],
					});
				}
			}
		);
	};

	const modalButtons = [
		{
			text: formatMessage({id: 'common.cancel'}),
			callback: closeModal.bind(this),
			color: BUTTON_COLOR.WHITE,
		},
		{
			text: formatMessage({id: 'common.export'}),
			preventDefaultClose: true,
			callback: onInvoiceExport,
			/*
			 * Two conditions, both required.
			 *
			 * isXeroExportBlocked is readiness: exporting, a client, an organisation, an account on every
			 * line. The rest is the cross-organisation guard, and it needs both of its halves -
			 * hasUnresolvableAccount asks whether the ids resolve against the loaded list, while
			 * isConfigStaleForClient asks whether that list belongs to the selected client at all, which
			 * the mount effect's stale closure can get wrong. xeroLineConfigLoading is belt and braces:
			 * during a load the list is [] and a stale id is already blocked by the first of those.
			 */
			disabled:
				isXeroExportBlocked({
					exporting,
					hasClient: !!selectedClient,
					tenantId: selectedXeroTenantId,
					lines: invoiceLines,
				}) ||
				(useLinesTable &&
					(xeroLineConfigLoading ||
						isConfigStaleForClient(requestedTenant.current, selectedXeroTenantId) ||
						hasUnresolvableAccount(invoiceLines, xeroLineConfig.accounts))),
		},
	];

	useEffect(() => {
		getXeroContacts().then(contRes => {
			if (!contRes) {
				closeModal();
				showModal({
					type: MODAL_TYPE.XERO_INACCESSIBLE,
					isAdminUser: hasPermission(PERMISSION_TYPE.MANAGE_ACCOUNT_SETTINGS),
				});
			}

			if (selectedClient) {
				const xeroContact = contRes?.find(xeroContact => xeroContact.ContactID === selectedClient.xeroContactId);
				/*
				 * Guarded because xeroLineConfigLoading starts TRUE whenever there is a client to load
				 * for, and a client with no matching Xero contact would throw on `xeroContact.TenantId`
				 * here, before loadXeroLineConfig could clear it - leaving the table reading "Loading..."
				 * for the life of the modal.
				 *
				 * Under the flag this is now the ordinary path for a client with no Xero contact, not an
				 * edge case: the optional chain on applyContactTaxRate's argument removed the earlier
				 * throw in getXeroContacts that used to close the modal with XERO_INACCESSIBLE first. The
				 * modal now stays open showing an empty configuration, and Export stays blocked - because
				 * no line can choose an account at all, which the every-line-needs-an-account rule catches.
				 * NOT because of hasUnresolvableAccount: on this path the lines carry no xeroAccount, and
				 * that predicate deliberately exempts them. It is what blocks the other case - switching
				 * away from a client that HAD a contact, where the lines do still carry accounts.
				 * Better than a modal that vanishes, and a deliberate flag-ON change rather than a side effect.
				 * Flag-off takes the else branch and throws exactly as it always did.
				 */
				if (!xeroContact && useLinesTable) {
					setXeroLineConfigLoading(false);
				} else {
					getXeroAccounts(xeroContact.TenantId);
					loadXeroLineConfig(xeroContact.TenantId, xeroContact?.DefaultTaxRate);
				}
			}
		});
	}, []);

	return (
		<GenericModal
			buttons={modalButtons}
			headerText={formatMessage({id: 'invoicing.export_to'}, {systemName: 'Xero'})}
			closeModal={closeModal}
			content={content}
		/>
	);
};

const xeroExportModalQuery = graphql`
	query xeroExportModal_Query($invoiceId: ID) {
		viewer {
			actualPersonId
			component(name: "xero_export_modal")
			...xeroExportModal_viewer @arguments(invoiceId: $invoiceId)
		}
	}
`;

export {xeroExportModalQuery};

export default createFragmentContainer(xeroExportModal, {
	viewer: graphql`
		fragment xeroExportModal_viewer on Viewer @argumentDefinitions(invoiceId: {type: "ID"}) {
			id
			backendId
			invoice(id: $invoiceId) {
				id
				invoiceReference
				companyInvoiceId
				name
				currency
				invoiceType
				status
				dueDay
				dueMonth
				dueYear
				createdDay
				createdMonth
				createdYear
				notes
				quickbooksId
				xeroId
				economicId
				client {
					id
				}
				entries(first: 100000000) {
					edges {
						node {
							id
							name
							quantity
							quantityText
							unitPrice
							discount
							tax
							description
							quantityLockedReason
							project {
								id
								companyProjectId
								name
								projectColor
								isInProjectGroup
								budgetType
								client {
									name
								}
								rateCard {
									currency
								}
							}
						}
					}
				}
			}
			projects {
				edges {
					node {
						id
						name
						projectColor
						isInProjectGroup
						companyProjectId
						projectGroupId
						budgetType
						status
						fullAccessToProject
					}
				}
			}
		}
	`,
});
